import DrawControl from '../../Mission/MissionView/MissionMap/DrawControl/DrawControl'
import drawStyles from '../../Mission/MissionView/MissionMap/DrawControl/drawStyles'
import CleaningPointsOnMap from '../../Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesNamesOnMap from '../../Mission/MissionView/MissionMap/Layers/CleaningRoutesNamesOnMap'
import CleaningRoutesOnMap from '../../Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import Title from 'components/Title/Title'
import { Feature, MultiLineString } from 'geojson'
import cloneDeep from 'lodash.clonedeep'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { MISSION_MAP_DRAWING_MODES, PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement, IRecordedRoute, IRoute } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

import RecordedRouteItem from './RecordedRouteItem/RecordedRouteItem'

import style from './RecordedRouteListView.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'

const RecordedRouteListView = ({
  recordedRoutes,
  onRoutesChanged,
  currentPage,
  totalCount,
  pageSize,
  onPageChanged,
  searchQuery,
  onSearchChanged,
}: {
  recordedRoutes: IRecordedRoute[]
  onRoutesChanged: () => void
  currentPage: number
  totalCount: number
  pageSize: number
  onPageChanged: (page: number) => void
  searchQuery: string
  onSearchChanged: (search: string) => void
}) => {
  const { t } = useTranslation()
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [highlightedZoneName, setHighlightedZoneName] = useState<string | undefined>(undefined)
  const [zoomZoneName, setZoomZoneName] = useState<string | undefined>(undefined)
  const [pageInput, setPageInput] = useState(currentPage.toString())
  const [editMode, setEditMode] = useState(false)
  const [editingRoute, setEditingRoute] = useState<IRecordedRoute | undefined>(undefined)
  const [savingProgress, setSavingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [mapMode, setMapMode] = useState<MISSION_MAP_DRAWING_MODES>(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)

  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const initialZoomDoneRef = useRef(false)

  useEffect(() => {
    initialZoomDoneRef.current = false
  }, [currentPage, searchQuery])

  useEffect(() => {
    if (map && recordedRoutes.length > 0 && !editingRoute && !initialZoomDoneRef.current) {
      zoomMapTo(map, [...recordedRoutes])
      initialZoomDoneRef.current = true
    }
  }, [map, recordedRoutes, editingRoute])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    let featuresToZoom: Feature[] = []

    if (zoomZoneName) {
      const index = recordedRoutes.findIndex(route => route.properties.name === highlightedZoneName)
      if (index !== -1 && recordedRoutes[index]) {
        featuresToZoom.push(recordedRoutes[index] as Feature)
      }
    }

    if (featuresToZoom.length > 0) {
      zoomMapTo(map, featuresToZoom)
    }
  }, [zoomZoneName])

  const prevEditingRouteIdRef = useRef<number | string | undefined>(undefined)

  useEffect(() => {
    if (editingRoute && map) {
      setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE)
      if (prevEditingRouteIdRef.current !== editingRoute.id) {
        zoomMapTo(map, [editingRoute])
        prevEditingRouteIdRef.current = editingRoute.id
      }
    } else {
      setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES)
      prevEditingRouteIdRef.current = undefined
    }
  }, [editingRoute, map])

  const onMapInited = () => {
    const map = mapRef.current?.getMap()
    if (map) {
      setMap(map)
    }

    const setHighlightedFeature = (name: string | undefined) => {
      setHighlightedZoneName(name)
    }
    map?.on('click', () => setHighlightedFeature(undefined))
    map?.on('click', 'gl-draw-cleaning-routes', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.name)
    )
    map?.on('click', 'recorded-route-text-layer', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.name)
    )
  }

  const onZoneClicked = (name: string) => {
    setHighlightedZoneName(name)
    setZoomZoneName(name)
  }

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / pageSize) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        onPageChanged(val)
      } else {
        setPageInput(currentPage.toString())
      }
    }
  }

  const startEditRoute = (route: IRecordedRoute) => {
    setEditingRoute(route)
    setEditMode(true)
  }

  const onUpdateRoute = (evt: { features: Feature[]; action: string }) => {
    const feature = evt.features[0]
    if (feature && editingRoute) {
      setEditingRoute({
        ...editingRoute,
        geometry: feature.geometry as MultiLineString,
      })
    }
  }

  const onSaveRoute = async () => {
    if (editingRoute && editingRoute.id) {
      setSavingProgress(PROGRESS_TYPES.WORK)
      try {
        await MainApi.updateRecordedRouteGeometry(editingRoute.id as number, editingRoute.geometry)
        setEditMode(false)
        setEditingRoute(undefined)
        onRoutesChanged()
      } catch (e) {
        console.error(e)
      } finally {
        setSavingProgress(PROGRESS_TYPES.IDLE)
      }
    }
  }

  const onCancelEdit = () => {
    setEditMode(false)
    setEditingRoute(undefined)
  }

  const onReverseRoute = () => {
    if (editingRoute && editingRoute.geometry) {
      const newGeometry = cloneDeep(editingRoute.geometry)
      if (newGeometry.type === 'MultiLineString') {
        newGeometry.coordinates = newGeometry.coordinates.map(line => [...line].reverse()).reverse()
      } else if (newGeometry.type === 'LineString') {
        // @ts-ignore
        newGeometry.coordinates = newGeometry.coordinates.reverse()
      }
      setEditingRoute({ ...editingRoute, geometry: newGeometry })
    }
  }

  const maxPage = Math.ceil(totalCount / pageSize) || 1

  const visibleRoutes = recordedRoutes.filter(r => r.id !== editingRoute?.id)

  const routesForPoints = recordedRoutes.map(r => (r.id === editingRoute?.id ? (editingRoute as IRecordedRoute) : r))

  return (
    <div className={style.container}>
      <div className={pageLayoutStyle.pageHeaderArea}>
        <div className={style.titleBlock}>
          <Title text={'Recorded routes'} />{' '}
        </div>
        <div className={style.controlsBlock}>
          <input
            className={style.searchInput}
            type="text"
            placeholder="Search routes..."
            value={searchQuery}
            onChange={e => onSearchChanged(e.target.value)}
            disabled={editMode}
          />
        </div>
      </div>
      <div className={style.main}>
        <Box className={style.box}>
          {!editMode ? (
            <div className={style.tableWrapper}>
              <div className={style.tableHeader}>
                <div></div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>#</div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>Name</div>
                <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Route length</div>
                <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>Recorded at</div>
                <div></div>
              </div>
              {recordedRoutes.length ? (
                <>
                  <div className={style.table}>
                    {recordedRoutes.map((route, index) => (
                      <RecordedRouteItem
                        key={route.id}
                        route={route}
                        index={(currentPage - 1) * pageSize + index + 1}
                        highlighted={route.properties.name === highlightedZoneName}
                        onRoutesChanged={onRoutesChanged}
                        onClicked={() => onZoneClicked(route.properties.name)}
                        onEditClicked={() => startEditRoute(route)}
                      />
                    ))}
                  </div>
                  <div className={style.pagination}>
                    <button
                      className={style.paginationButton}
                      onClick={() => onPageChanged(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      &larr;
                    </button>
                    <span className={style.pageInfo}>
                      <input
                        className={style.pageInput}
                        value={pageInput}
                        onChange={e => setPageInput(e.target.value)}
                        onKeyDown={handlePageJump}
                        onBlur={() => setPageInput(currentPage.toString())}
                      />
                      / {maxPage}
                    </span>
                    <button
                      className={style.paginationButton}
                      onClick={() => onPageChanged(currentPage + 1)}
                      disabled={currentPage >= maxPage}
                    >
                      &rarr;
                    </button>
                  </div>
                </>
              ) : (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>{t('view_zones.empty_zone_list')}</div>
                  <div className={style.emptyDescription}>{t('view_zones.empty_zone_list_description')}</div>
                </div>
              )}
            </div>
          ) : (
            <div className={style.editPanel}>
              <div className={style.editTitle}>Editing of the {editingRoute?.properties?.name}</div>
              <div className={style.topActions}>
                <Button.Outlined
                  onClick={onReverseRoute}
                  disabled={savingProgress === PROGRESS_TYPES.WORK}
                  className={style.button}
                >
                  {t('common.reverse')}
                </Button.Outlined>
              </div>
              <div className={style.buttons}>
                <Button.Outlined
                  className={style.button}
                  onClick={onCancelEdit}
                  disabled={savingProgress === PROGRESS_TYPES.WORK}
                >
                  {t('common.cancel')}
                </Button.Outlined>
                <Button.Standart
                  className={style.button}
                  onClick={onSaveRoute}
                  loading={savingProgress === PROGRESS_TYPES.WORK}
                  disabled={savingProgress === PROGRESS_TYPES.WORK}
                >
                  {t('common.save')}
                </Button.Standart>
              </div>
            </div>
          )}
        </Box>
        <MapboxCommon ref={mapRef} disableRuler={true} overlayItems={[]} onMapInited={onMapInited} maxZoom={23}>
          <CleaningRoutesOnMap cleaningRoutes={visibleRoutes} />
          <CleaningRoutesNamesOnMap cleaningRoutes={visibleRoutes} />
          <CleaningPointsOnMap
            cleaningRoutes={routesForPoints}
            launchPoints={[]}
            editingRouteId={editingRoute?.id as number}
          />
          {editMode && editingRoute && (
            <DrawControl
              userProperties={true}
              displayControlsDefault={false}
              onCreate={() => {}}
              onUpdate={onUpdateRoute}
              onDelete={() => {}}
              mode={mapMode}
              tempZoneRoute={{ ...editingRoute, id: String(editingRoute.id) } as unknown as IRoute}
              tempZone={undefined}
              tempLaunchPoint={undefined}
              tempTransfer={undefined}
              tempMissionPoint={undefined}
              styles={drawStyles}
              setEditingTempLaunchPoint={() => {}}
              setEditingTempZone={() => {}}
              setEditingTempZoneRoute={() => {}}
            />
          )}
        </MapboxCommon>
      </div>
    </div>
  )
}

export default RecordedRouteListView
